import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Tabs,
  Alert,
  Select,
  Typography,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  SettingOutlined,
  SecurityScanOutlined,
  DollarOutlined,
  CloudOutlined,
  NotificationOutlined,
  ApiOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface SystemSettings {
  general: {
    platformName: string;
    supportEmail: string;
    timezone: string;
    dateFormat: string;
    currency: string;
  };
  security: {
    maxLoginAttempts: number;
    lockoutDuration: number;
    passwordMinLength: number;
    requireSpecialChar: boolean;
    sessionTimeout: number;
    twoFactorAuth: boolean;
  };
  billing: {
    defaultCurrency: string;
    taxRate: number;
    invoicePrefix: string;
    gracePeriodDays: number;
    autoSuspendEnabled: boolean;
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    welcomeEmailTemplate: string;
    paymentReminderTemplate: string;
    suspensionWarningTemplate: string;
  };
  api: {
    rateLimit: number;
    maxUploadSize: number;
    corsEnabled: boolean;
    webhookTimeout: number;
  };
}

const defaultSettings: SystemSettings = {
  general: {
    platformName: 'ERP Gold',
    supportEmail: 'support@erpgold.vn',
    timezone: 'Asia/Ho_Chi_Minh',
    dateFormat: 'DD/MM/YYYY',
    currency: 'VND',
  },
  security: {
    maxLoginAttempts: 5,
    lockoutDuration: 30,
    passwordMinLength: 8,
    requireSpecialChar: true,
    sessionTimeout: 60,
    twoFactorAuth: false,
  },
  billing: {
    defaultCurrency: 'VND',
    taxRate: 10,
    invoicePrefix: 'INV',
    gracePeriodDays: 7,
    autoSuspendEnabled: true,
  },
  notifications: {
    emailEnabled: true,
    smsEnabled: false,
    welcomeEmailTemplate: 'Welcome to ERP Gold!',
    paymentReminderTemplate: 'Your payment is due soon.',
    suspensionWarningTemplate: 'Your account will be suspended due to non-payment.',
  },
  api: {
    rateLimit: 1000,
    maxUploadSize: 10,
    corsEnabled: true,
    webhookTimeout: 30,
  },
};

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [generalForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [billingForm] = Form.useForm();
  const [notificationsForm] = Form.useForm();
  const [apiForm] = Form.useForm();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      // Mock API call - replace with actual API
      // const response = await fetch('/api/v1/settings');
      // const data = await response.json();
      // setSettings(data);
      // Initialize forms with data
      setSettings(defaultSettings);
      generalForm.setFieldsValue(defaultSettings.general);
      securityForm.setFieldsValue(defaultSettings.security);
      billingForm.setFieldsValue(defaultSettings.billing);
      notificationsForm.setFieldsValue(defaultSettings.notifications);
      apiForm.setFieldsValue(defaultSettings.api);
    } catch {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [apiForm, billingForm, generalForm, notificationsForm, securityForm]);

  useEffect(() => {
    // Load settings from API
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (section: keyof SystemSettings, form: any) => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      // Mock API call - replace with actual API
      // await fetch(`/api/v1/settings/${section}`, {
      //   method: 'PUT',
      //   body: JSON.stringify(values),
      // });
      setSettings((prev) => ({ ...prev, [section]: values }));
      message.success('Settings saved successfully');
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>System Settings</Title>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchSettings}
            loading={loading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={<span><SettingOutlined /> General</span>}
          key="general"
        >
          <Card loading={loading}>
            <Form
              form={generalForm}
              layout="vertical"
              initialValues={settings.general}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="platformName"
                    label="Platform Name"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="ERP Gold" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="supportEmail"
                    label="Support Email"
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input placeholder="support@example.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="timezone" label="Default Timezone">
                    <Select>
                      <Option value="Asia/Ho_Chi_Minh">Asia/Ho Chi Minh</Option>
                      <Option value="Asia/Bangkok">Asia/Bangkok</Option>
                      <Option value="Asia/Singapore">Asia/Singapore</Option>
                      <Option value="UTC">UTC</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dateFormat" label="Date Format">
                    <Select>
                      <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                      <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                      <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="currency" label="Default Currency">
                <Select>
                  <Option value="VND">VND - Vietnamese Dong</Option>
                  <Option value="USD">USD - US Dollar</Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('general', generalForm)}
                  loading={saving}
                >
                  Save General Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><SecurityScanOutlined /> Security</span>}
          key="security"
        >
          <Card loading={loading}>
            <Alert
              message="Security Settings"
              description="Configure security policies for all platform users."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form
              form={securityForm}
              layout="vertical"
              initialValues={settings.security}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="maxLoginAttempts"
                    label="Max Login Attempts"
                    rules={[{ required: true }]
                    }
                  >
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="lockoutDuration"
                    label="Lockout Duration (minutes)"
                  >
                    <InputNumber min={5} max={1440} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="passwordMinLength"
                    label="Min Password Length"
                  >
                    <InputNumber min={6} max={32} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sessionTimeout"
                    label="Session Timeout (minutes)"
                  >
                    <InputNumber min={5} max={480} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="requireSpecialChar"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Require Special Chars" unCheckedChildren="No Special Chars" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="twoFactorAuth"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="2FA Enabled" unCheckedChildren="2FA Disabled" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('security', securityForm)}
                  loading={saving}
                >
                  Save Security Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><DollarOutlined /> Billing</span>}
          key="billing"
        >
          <Card loading={loading}>
            <Form
              form={billingForm}
              layout="vertical"
              initialValues={settings.billing}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="defaultCurrency" label="Default Currency">
                    <Select>
                      <Option value="VND">VND - Vietnamese Dong</Option>
                      <Option value="USD">USD - US Dollar</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="taxRate" label="Tax Rate (%)">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="invoicePrefix" label="Invoice Prefix">
                    <Input placeholder="INV" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gracePeriodDays" label="Grace Period (days)">
                    <InputNumber min={0} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="autoSuspendEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="Auto-Suspend Enabled" unCheckedChildren="Auto-Suspend Disabled" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('billing', billingForm)}
                  loading={saving}
                >
                  Save Billing Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><NotificationOutlined /> Notifications</span>}
          key="notifications"
        >
          <Card loading={loading}>
            <Form
              form={notificationsForm}
              layout="vertical"
              initialValues={settings.notifications}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="emailEnabled" valuePropName="checked">
                    <Switch checkedChildren="Email Enabled" unCheckedChildren="Email Disabled" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="smsEnabled" valuePropName="checked">
                    <Switch checkedChildren="SMS Enabled" unCheckedChildren="SMS Disabled" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="welcomeEmailTemplate" label="Welcome Email Template">
                <TextArea rows={4} placeholder="Welcome email template..." />
              </Form.Item>
              <Form.Item name="paymentReminderTemplate" label="Payment Reminder Template">
                <TextArea rows={4} placeholder="Payment reminder template..." />
              </Form.Item>
              <Form.Item name="suspensionWarningTemplate" label="Suspension Warning Template">
                <TextArea rows={4} placeholder="Suspension warning template..." />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('notifications', notificationsForm)}
                  loading={saving}
                >
                  Save Notification Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><ApiOutlined /> API</span>}
          key="api"
        >
          <Card loading={loading}>
            <Form
              form={apiForm}
              layout="vertical"
              initialValues={settings.api}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="rateLimit" label="Rate Limit (requests/min)">
                    <InputNumber min={100} max={10000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxUploadSize" label="Max Upload Size (MB)">
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="webhookTimeout" label="Webhook Timeout (seconds)">
                    <InputNumber min={5} max={300} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="corsEnabled" valuePropName="checked">
                    <Switch checkedChildren="CORS Enabled" unCheckedChildren="CORS Disabled" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('api', apiForm)}
                  loading={saving}
                >
                  Save API Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><CloudOutlined /> System Info</span>}
          key="info"
        >
          <Card>
            <Descriptions title="Platform Information" bordered column={2}>
              <Descriptions.Item label="Version">1.0.0</Descriptions.Item>
              <Descriptions.Item label="Build Date">2024-04-18</Descriptions.Item>
              <Descriptions.Item label="Node Version">20.x</Descriptions.Item>
              <Descriptions.Item label="Database">MongoDB 7.0</Descriptions.Item>
              <Descriptions.Item label="Redis" span={2}>
                <Badge status="success" text="Connected" />
              </Descriptions.Item>
              <Descriptions.Item label="API Server" span={2}>
                <Badge status="success" text="Running (Port 3100)" />
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
